import { memo, useState } from 'react'
import Typography from '@mui/material/Typography'
import IconButton from '@mui/material/IconButton'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import CloseIcon from '@mui/icons-material/Close'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import Collapse from '@mui/material/Collapse'
import { styled } from '@mui/material/styles'
import { observer } from 'mobx-react'
import useVoucherAmount from './useVoucherAmount'
import illustration from './assets/illustration@2x.png'
import { useClientDetails } from 'stores/utils/hooks/useClientDetails'

const BannerContainer = styled('div')(({ theme }) => ({
  background: 'linear-gradient(270deg, #1B5426 0%, #277634 100%)',
  color: '#fff',
  overflow: 'hidden',
  padding: theme.spacing(0, 1.5)
}))

const MaxWidthWrapper = styled('div')(({ theme }) => ({
  position: 'relative',
  overflow: 'hidden',
  [theme.breakpoints.up('sm')]: {
    maxWidth: 532,
    margin: '0 auto'
  }
}))

const ContentContainer = styled('div')({
  display: 'flex',
  flexDirection: 'column'
})

const Illustration = styled('img')<{ expanded?: boolean }>(({ expanded }) => ({
  position: 'absolute',
  right: -10,
  bottom: 0,
  top: -5,
  height: 102,
  width: 'auto',
  pointerEvents: 'none',
  opacity: 1,
  transition: 'bottom 0.3s ease'
}))

const HeaderRow = styled('div')({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  minHeight: 48,
  zIndex: 1,
  position: 'relative'
})

const TitleRow = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  flex: 1
})

const Title = styled(Typography)({
  fontWeight: 700,
  fontSize: '15px',
  color: '#fff',
  lineHeight: 1.3
})

const BodyText = styled(Typography)(({ theme }) => ({
  fontSize: '14px',
  fontWeight: 400,
  color: '#fff',
  lineHeight: 1.4,
  paddingBottom: theme.spacing(1.5),
  position: 'relative',
  zIndex: 1
}))

const LinkText = styled('span')({
  textDecoration: 'underline',
  cursor: 'pointer'
})

const UnderlinedLink = styled('a')({
  textDecoration: 'underline',
  cursor: 'pointer',
  color: '#fff'
})

const VoucherActivatedBanner = () => {
  const [isExpanded, setIsExpanded] = useState(false)
  const voucherAmount = useVoucherAmount()
  const clientDetails = useClientDetails()

  const title =
    voucherAmount != null
      ? `Ihr ${voucherAmount} € Gutschein ist aktiviert!`
      : 'Ihr Gutschein ist aktiviert!'
  const body = `aktivieren, Kreditvertrag über smava.de abschließen & ${voucherAmount} € Gutschein per E-Mail erhalten`
  const termsAndConditionsUrl = clientDetails?.urls?.termsAndConditions

  return (
    <BannerContainer>
      <MaxWidthWrapper>
        <Illustration
          expanded={isExpanded}
          src={illustration}
          alt=""
          aria-hidden="true"
        />
        <ContentContainer>
          <HeaderRow>
            <TitleRow>
              <CheckCircleIcon
                sx={{ color: '#fff', fontSize: 22, flexShrink: 0 }}
              />
              <Title variant="body1">{title}</Title>
            </TitleRow>
            <IconButton
              size="small"
              onClick={() => setIsExpanded((prev) => !prev)}
              aria-label={isExpanded ? 'Schlie\u00dfen' : 'Mehr erfahren'}
              sx={{ color: '#fff', ml: 1 }}
            >
              {isExpanded ? (
                <CloseIcon fontSize="small" />
              ) : (
                <InfoOutlinedIcon fontSize="small" sx={{ color: '#C5E8C7' }} />
              )}
            </IconButton>
          </HeaderRow>
          <Collapse in={isExpanded} unmountOnExit>
            <BodyText variant="body2">
              {'Gutschein entsprechend den '}
              <UnderlinedLink
                href={termsAndConditionsUrl}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Bedingungen (öffnet in neuem Fenster)"
              >
                {'Bedingungen'}
              </UnderlinedLink>
              {` ${body}`}
            </BodyText>
          </Collapse>
        </ContentContainer>
      </MaxWidthWrapper>
    </BannerContainer>
  )
}

export default memo(observer(VoucherActivatedBanner))
